import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  Smartphone,
  BarChart3,
  Target,
  FileText,
  FileImage,
  Building,
  Building2,
  Users,
  Package,
  Calendar,
  TrendingUp,
  Truck,
  Settings,
  X,
  UserPlus,
  ChevronDown,
  ChevronRight,
  LogOut,
  Images,
  Lock,
  RefreshCw,
  Shield,
  Calculator,
  ClipboardList,
  DollarSign,
  AlertCircle,
  Palette,
  ArrowRightLeft,
  StickyNote,
  Radar,
  CheckCircle,
  FlaskConical,
  BookOpen,
  Eye,
  Tags,
  ClipboardCheck,
  Store,
  FileWarning,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { LoadingLink } from "@/components/loading-link";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuthUser } from "@/hooks/useAuthUser";
import { signOut } from "@/lib/authBootstrap";
import { useAuth } from "@/lib/auth-store";
import { canAccessPhotoChecksAdvanced } from "@/lib/photoChecksAccess";
import { useGlobalTheme } from "@/hooks/useGlobalTheme";
import type { User } from "@shared/schema";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: any;
  isSubItem?: boolean;
  children?: NavigationItem[];
}

const adminNavigation: NavigationItem[] = [
  { name: "Accounts", href: "/accounts", icon: Building2 },
  { name: "Bookings", href: "/admin/bookings", icon: StickyNote },
  { name: "Campaigns", href: "/campaigns", icon: BarChart3 },
  { name: "Cycles", href: "/cycles", icon: RefreshCw },
  {
    name: "Tasks",
    icon: Target,
    children: [
      { name: "Task Distribution", href: "/sign-off-v2", icon: FileText },
      { name: "Assignments", href: "/tasks/assignments", icon: ClipboardList },
      { name: "Postings", href: "/admin/postings", icon: BookOpen },
      { name: "Removals", href: "/admin/removals-preview", icon: Eye },
    ]
  },
  {
    name: "Proof of Posting",
    icon: FileImage,
    children: [
      { name: "Cockpit", href: "/admin/activation-cockpit/dashboard", icon: Radar },
      { name: "Proof of Posting", href: "/proof-of-posting-enhanced", icon: Smartphone },
      { name: "Gallery", href: "/image-gallery", icon: Images },
      { name: "Compliance", href: "/proof-of-posting-enhanced/compliance", icon: CheckCircle },
      { name: "Reviews", href: "/proof-of-posting-enhanced/reviews", icon: FileWarning },
    ]
  },
  { name: "Audits", href: "/admin/audits", icon: ClipboardCheck },
  {
    name: "Operators",
    icon: Truck,
    children: [
      { name: "Operator Tasks", href: "/admin/task-templates", icon: FileText },
      { name: "Task Rules & Rates", href: "/admin/operator-tasks", icon: Settings },
      { name: "Payment Reports", href: "/admin/operator-payment-reports", icon: DollarSign },
      { name: "Operator Reassignment", href: "/admin/operator-reassignment", icon: ArrowRightLeft },
    ]
  },
  {
    name: "Admin",
    icon: Settings,
    children: [
      { name: "Users", href: "/users", icon: Users },
      { name: "Partner access", href: "/admin/partner-access", icon: Shield },
      { name: "Snapshots", href: "/admin/snapshots", icon: Package },
      { name: "Retailers", href: "/retailer-management", icon: Building },
      { name: "General", href: "/campaign-status", icon: FileText },
      {
        name: "Photo Checks",
        icon: FlaskConical,
        children: [
          { name: "Home", href: "/admin/ai", icon: FlaskConical },
          { name: "Photo standards", href: "/admin/ai/checks", icon: FileText },
          { name: "Know our media", href: "/admin/ai/verification", icon: Eye },
          { name: "Try a change", href: "/admin/ai/results", icon: FlaskConical },
        ],
      },
    ]
  },
  {
    name: "SuperAdmin",
    icon: Lock,
    children: [
      { name: "SuperAdmin Management", href: "/superadmin-management", icon: Users },
      { name: "Emergency Bug Monitor", href: "/admin/logs", icon: AlertCircle },
    ]
  },
];

const superAdminNavigation: NavigationItem[] = [
  { name: "Accounts", href: "/accounts", icon: Building2 },
  { name: "Bookings", href: "/admin/bookings", icon: StickyNote },
  { name: "Campaigns", href: "/campaigns", icon: BarChart3 },
  { name: "Cycles", href: "/cycles", icon: RefreshCw },
  {
    name: "Tasks",
    icon: Target,
    children: [
      { name: "Task Distribution", href: "/sign-off-v2", icon: FileText },
      { name: "Assignments", href: "/tasks/assignments", icon: ClipboardList },
      { name: "Postings", href: "/admin/postings", icon: BookOpen },
      { name: "Removals", href: "/admin/removals-preview", icon: Eye },
    ]
  },
  {
    name: "Proof of Posting",
    icon: FileImage,
    children: [
      { name: "Cockpit", href: "/admin/activation-cockpit/dashboard", icon: Radar },
      { name: "Proof of Posting", href: "/proof-of-posting-enhanced", icon: Smartphone },
      { name: "Gallery", href: "/image-gallery", icon: Images },
      { name: "Compliance", href: "/proof-of-posting-enhanced/compliance", icon: CheckCircle },
      { name: "Reviews", href: "/proof-of-posting-enhanced/reviews", icon: FileWarning },
    ]
  },
  { name: "Audits", href: "/admin/audits", icon: ClipboardCheck },
  {
    name: "Operators",
    icon: Truck,
    children: [
      { name: "Operator Tasks", href: "/admin/task-templates", icon: FileText },
      { name: "Task Rules & Rates", href: "/admin/operator-tasks", icon: Settings },
      { name: "Payment Reports", href: "/admin/operator-payment-reports", icon: DollarSign },
      { name: "Operator Reassignment", href: "/admin/operator-reassignment", icon: ArrowRightLeft },
    ]
  },
  {
    name: "Admin",
    icon: Settings,
    children: [
      { name: "Users", href: "/users", icon: Users },
      { name: "Partner access", href: "/admin/partner-access", icon: Shield },
      { name: "Retailers", href: "/retailer-management", icon: Building },
      { name: "General", href: "/campaign-status", icon: FileText },
      { name: "Advertisers", href: "/advertisers", icon: Building2 },
      {
        name: "Photo Checks",
        icon: FlaskConical,
        children: [
          { name: "Home", href: "/admin/ai", icon: FlaskConical },
          { name: "Photo standards", href: "/admin/ai/checks", icon: FileText },
          { name: "Know our media", href: "/admin/ai/verification", icon: Eye },
          { name: "Try a change", href: "/admin/ai/results", icon: FlaskConical },
        ],
      },
    ]
  },
  {
    name: "Super Admin",
    icon: Lock,
    children: [
      { name: "SuperAdmin Management", href: "/superadmin-management", icon: Users },
      { name: "Emergency Bug Monitor", href: "/admin/logs", icon: AlertCircle },
      { name: "Business Rules", href: "/super-admin/business-rules", icon: Settings },
      { name: "Look & Feel", href: "/super-admin/look-and-feel", icon: Palette },
      { name: "Store Profiles", href: "/super-admin/store-profiles", icon: Store },
    ]
  },
];

const operatorNavigation: NavigationItem[] = [
  { name: "Field Operations", href: "/", icon: Smartphone },
  {
    name: "Tasks",
    icon: Target,
    children: [
      { name: "Task Distribution", href: "/sign-off-v2", icon: FileText },
    ]
  },
  {
    name: "Proof of Posting",
    icon: FileImage,
    children: [
      { name: "Proof of Posting", href: "/proof-of-posting-enhanced", icon: Smartphone },
      { name: "Gallery", href: "/image-gallery", icon: Images },
      { name: "Compliance", href: "/proof-of-posting-enhanced/compliance", icon: CheckCircle },
      { name: "Reviews", href: "/proof-of-posting-enhanced/reviews", icon: FileWarning },
    ]
  },
];

const reviewerNavigation: NavigationItem[] = [
  {
    name: "Proof of Posting",
    icon: FileImage,
    children: [
      { name: "Cockpit", href: "/admin/activation-cockpit/review", icon: Radar },
      { name: "Proof of Posting", href: "/proof-of-posting-enhanced", icon: Smartphone },
      { name: "Gallery", href: "/image-gallery", icon: Images },
      { name: "Compliance", href: "/proof-of-posting-enhanced/compliance", icon: CheckCircle },
      { name: "Reviews", href: "/proof-of-posting-enhanced/reviews", icon: FileWarning },
    ]
  },
  { name: "Campaigns", href: "/campaigns", icon: BarChart3 },
  { name: "Cycles", href: "/cycles", icon: RefreshCw },
];

/** Exact match, plus nested routes for known workspace roots. */
const NESTED_NAV_PREFIXES = ['/retailer-management', '/accounts', '/admin/partner-access'] as const;

function isNavHrefActive(location: string, href?: string): boolean {
  if (!href) return false;
  if (location === href) return true;
  if (
    NESTED_NAV_PREFIXES.some((prefix) => prefix === href)
    && location.startsWith(`${href}/`)
  ) {
    return true;
  }
  return false;
}

function navigationItemContainsActiveRoute(
  item: NavigationItem,
  location: string,
): boolean {
  if (item.href && isNavHrefActive(location, item.href)) return true;
  return item.children?.some((child) => navigationItemContainsActiveRoute(child, location)) ?? false;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [location, setLocation] = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { dispatch } = useAuth();

  const { user } = useAuthUser();
  const { data: globalTheme } = useGlobalTheme();

  // Determine navigation based on user roles (check both string role and array roles)
  const isSuperAdmin = user?.roles?.includes("superadmin") || user?.role === "superadmin";
  const isAdmin = user?.roles?.includes("admin") || user?.role === "admin";
  const isReviewer = user?.roles?.includes("reviewer") || user?.role === "reviewer";
  const showPhotoChecksAdvanced = canAccessPhotoChecksAdvanced(user);

  const withPhotoChecksAdvanced = (items: NavigationItem[]): NavigationItem[] => (
    items.map((item) => {
      if (item.name !== "Admin" || !item.children) return item;
      return {
        ...item,
        children: item.children.map((child) => {
          if (child.name !== "Photo Checks" || !child.children) return child;
          if (!showPhotoChecksAdvanced) return child;
          if (child.children.some((c) => c.href === "/admin/ai/advanced")) return child;
          return {
            ...child,
            children: [
              ...child.children,
              { name: "Advanced", href: "/admin/ai/advanced", icon: Lock },
            ],
          };
        }),
      };
    })
  );

  const navigation: NavigationItem[] = useMemo(
    () => withPhotoChecksAdvanced(
      isSuperAdmin ? superAdminNavigation :
        isAdmin ? adminNavigation :
          isReviewer ? reviewerNavigation :
            operatorNavigation,
    ),
    [isSuperAdmin, isAdmin, isReviewer, showPhotoChecksAdvanced],
  );

  useEffect(() => {
    const parentsToExpand = navigation
      .filter((item) => item.children && navigationItemContainsActiveRoute(item, location))
      .map((item) => item.name);

    if (parentsToExpand.length === 0) return;

    setExpandedItems((prev) => {
      const missing = parentsToExpand.filter((name) => !prev.includes(name));
      if (missing.length === 0) return prev;
      return [...prev, ...missing];
    });
  }, [location, navigation]);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      console.log('[AUTH_SUPABASE] Sidebar: Signing out...');
      await signOut();
    },
    onSuccess: () => {
      console.log('[AUTH_SUPABASE] Sidebar: Sign out successful');
      // Hard reload to avoid flash of wrong route during auth state transition
      window.location.href = '/login';
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
    },
    onError: (error: any) => {
      console.error('[AUTH_SUPABASE] Sidebar: Sign out failed:', error);
      toast({
        title: "Error",
        description: error.message || "Logout failed",
        variant: "destructive",
      });
    },
  });

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[45] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <nav
        className={cn(
          "fixed left-0 top-0 h-full w-64 shadow-lg transform transition-transform duration-300 ease-in-out z-[50]",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        style={{
          backgroundColor: 'var(--navigation-bg, white)',
          color: 'var(--navigation-text, hsl(var(--foreground)))'
        }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {globalTheme?.logoUrl ? (
                <img
                  src={globalTheme.logoUrl}
                  alt="Logo"
                  className="h-8 max-w-[200px] object-contain"
                  onError={(e) => {
                    // Fallback to text if image fails to load
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
              ) : null}
              <h1 className={cn(
                "text-xl font-semibold",
                globalTheme?.logoUrl && "hidden"
              )}>
                ReActor
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="lg:hidden"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>


        {/* Navigation */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <div className="space-y-2">
            {navigation.map((item, index) => {
              const Icon = item.icon;
              const isExpanded = expandedItems.includes(item.name);

              if (item.children) {
                // Parent item with children
                return (
                  <div key={item.name}>
                    <div
                      className="flex items-center justify-between py-3 px-4 text-sm font-medium rounded-lg hover:bg-black/5 transition-colors cursor-pointer"
                      onClick={() => toggleExpanded(item.name)}
                    >
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-3 opacity-70" />
                        <span>{item.name}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      ) : (
                        <ChevronRight className="h-4 w-4 opacity-50" />
                      )}
                    </div>

                    {/* Sub-items */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isActive = isNavHrefActive(location, subItem.href);
                          const isSubExpanded = expandedItems.includes(subItem.name);

                          if (subItem.children) {
                            // Nested sub-item with children
                            return (
                              <div key={subItem.name}>
                                <div
                                  className="flex items-center justify-between py-2 px-4 text-sm rounded-lg hover:bg-black/5 transition-colors cursor-pointer"
                                  onClick={() => toggleExpanded(subItem.name)}
                                >
                                  <div className="flex items-center">
                                    <SubIcon className="h-4 w-4 mr-3 opacity-70" />
                                    <span className="opacity-90">{subItem.name}</span>
                                  </div>
                                  {isSubExpanded ? (
                                    <ChevronDown className="h-3 w-3 opacity-50" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3 opacity-50" />
                                  )}
                                </div>

                                {/* Nested sub-items */}
                                {isSubExpanded && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {subItem.children.map((nestedItem) => {
                                      const NestedIcon = nestedItem.icon;
                                      const isNestedActive = isNavHrefActive(location, nestedItem.href);

                                      return (
                                        <Link key={nestedItem.name} href={nestedItem.href!}>
                                          <div
                                            className={cn(
                                              "flex items-center py-2 px-4 text-sm rounded-lg hover:bg-black/5 transition-colors cursor-pointer",
                                              isNestedActive
                                                ? "font-semibold border-r-2 border-white/50"
                                                : "opacity-90"
                                            )}
                                            style={isNestedActive ? { backgroundColor: 'var(--nav-active, rgba(255,255,255,0.2))' } : undefined}
                                          >
                                            <NestedIcon className="h-4 w-4 mr-3" />
                                            <span>{nestedItem.name}</span>
                                          </div>
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            // Regular sub-item
                            return (
                              <Link key={subItem.name} href={subItem.href!}>
                                <div
                                  className={cn(
                                    "flex items-center py-2 px-4 text-sm rounded-lg hover:bg-black/5 transition-colors cursor-pointer",
                                    isActive
                                      ? "font-semibold border-r-2 border-white/50"
                                      : "opacity-90"
                                  )}
                                  style={isActive ? { backgroundColor: 'var(--nav-active, rgba(255,255,255,0.2))' } : undefined}
                                >
                                  <SubIcon className="h-4 w-4 mr-3" />
                                  <span>{subItem.name}</span>
                                </div>
                              </Link>
                            );
                          }
                        })}
                      </div>
                    )}
                  </div>
                );
              } else {
                // Direct link item (no children)
                const isActive = isNavHrefActive(location, item.href);

                return (
                  <Link key={item.name} href={item.href!}>
                    <div
                      className={cn(
                        "flex items-center py-3 px-4 text-sm font-medium rounded-lg hover:bg-black/5 transition-colors cursor-pointer",
                        isActive
                          ? "font-semibold border-r-2 border-white/50"
                          : ""
                      )}
                      style={isActive ? { backgroundColor: 'var(--nav-active, rgba(255,255,255,0.2))' } : undefined}
                    >
                      <Icon className="h-5 w-5 mr-3 opacity-70" />
                      <span>{item.name}</span>
                    </div>
                  </Link>
                );
              }
            })}
          </div>
        </div>

        {/* User profile and actions */}
        <div
          className="absolute bottom-0 left-0 right-0 p-4 border-t space-y-3"
          style={{
            backgroundColor: 'var(--navigation-bg, white)',
            borderTopColor: 'rgba(255, 255, 255, 0.1)',
            borderTopWidth: '1px',
            opacity: 0.95
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {(user?.name || user?.username || user?.email || "U").charAt(0).toUpperCase()}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.name || user?.username || user?.email || "User"}
                </p>
                <p className="text-xs opacity-70 capitalize">
                  {user?.role || "Loading..."}
                </p>
              </div>
            </div>
          </div>

          {/* User actions */}
          <div className="flex gap-2">
            <Link href="/change-password" className="flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs"
                onClick={onClose}
                title="Change Password"
              >
                <Lock className="h-3 w-3 mr-2" />
                Password
              </Button>
            </Link>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
              className="px-3"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>
    </>
  );
}
